import React from "react";
import FAQSection from '../components/FAQSection';
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const SalePurchase = () => {
    return (
        <><Helmet>
            <title>Garage Sale & Purchase Services | MM AutoTech UK</title>
            <meta
                name="description"
                content="Looking to buy or sell a garage in the UK? MM AutoTech helps with sale, purchase, and valuation services for garage businesses." />
            <link rel="canonical" href="https://mmautotech.com/services/sale-purchase" />
        </Helmet><div className="max-w-7xl mx-auto px-6 py-16 space-y-19">

                {/* Hero Section */}
                <section className="flex flex-col md:flex-row items-center gap-12">
                    <div className="md:w-1/2">
                        <h1 className="text-4xl font-bold mb-6 text-blue-900">Explore Quality Used Cars</h1>
                        <p className="text-gray-700 mb-6">
                            Welcome to MM AutoTech – your trusted partner for buying and selling used vehicles. Explore a wide range of certified pre-owned cars or get a quick and fair quote for your vehicle.
                        </p>
                        <Link to="/contact">
                            <button className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded shadow transition">
                                Book an Appointment
                            </button>
                        </Link>
                    </div>
                    <div className="md:w-1/2">
                        <img
                            src="https://www.shutterstock.com/image-photo/happy-multiracial-couple-communicating-saleswoman-600nw-2419216085.jpg"
                            alt="Car Sales"
                            className="rounded-lg shadow-lg object-cover w-full h-72 md:h-full" />
                    </div>
                </section>

                <section className="py-16">
                    <h2 className="text-3xl font-bold mb-12 text-blue-900">Why Buy Used Cars?</h2>
                    <div className="grid md:grid-cols-2 gap-12 relative">

                        {/* Left Column */}
                        <div className="space-y-16 relative before:absolute before:top-0 before:bottom-0 before:left-5 before:w-0.5 before:bg-gray-300">
                            {[
                                {
                                    title: "Cost Savings",
                                    description: "Used cars generally come at a lower price compared to new cars, allowing you to save money upfront and potentially lower monthly payments if financing.",
                                },
                                {
                                    title: "Depreciation",
                                    description: "New cars depreciate significantly in their first few years, but used cars have already experienced much of this depreciation. This means you won’t lose as much value over time.",
                                },
                                {
                                    title: "Lower Insurance Costs",
                                    description: "Insurance premiums are often lower for used cars because they typically cost less to replace or repair than new models.",
                                },
                            ].map((item, idx) => (
                                <div key={idx} className="relative pl-14">
                                    <div className="absolute left-0 top-1 w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center bg-white text-blue-600 shadow">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path d="M3 13h2l1 2h12l1-2h2m-4-4h2.5a1.5 1.5 0 011.5 1.5V13H4v-2.5A1.5 1.5 0 015.5 9H8m4-2v2m0 0H8m4 0h4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-lg text-blue-900 mb-1">{item.title}</h4>
                                    <p className="text-gray-700 text-sm">{item.description}</p>
                                </div>
                            ))}
                        </div>

                        {/* Right Column */}
                        <div className="space-y-16 relative before:absolute before:top-0 before:bottom-0 before:left-5 before:w-0.5 before:bg-gray-300">
                            {[
                                {
                                    title: "Certified Pre-Owned Options",
                                    description: "Many dealerships, including MMAutoTech, offer certified pre-owned (CPO) vehicles. These undergo rigorous inspections and often come with extended warranties, providing added peace of mind.",
                                },
                                {
                                    title: "Technology and Features",
                                    description: "Modern vehicles are built to last longer and offer advanced features. Buying used allows you to access these technologies at a more affordable price point.",
                                },
                                {
                                    title: "Variety and Availability",
                                    description: "When buying used, you have access to a wider variety of makes, models, and features within your budget range. You can find vehicles that may no longer be produced or are harder to find new.",
                                },
                            ].map((item, idx) => (
                                <div key={idx} className="relative pl-14">
                                    <div className="absolute left-0 top-1 w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center bg-white text-blue-600 shadow">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path d="M3 13h2l1 2h12l1-2h2m-4-4h2.5a1.5 1.5 0 011.5 1.5V13H4v-2.5A1.5 1.5 0 015.5 9H8m4-2v2m0 0H8m4 0h4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </div>
                                    <h4 className="font-semibold text-lg text-blue-900 mb-1">{item.title}</h4>
                                    <p className="text-gray-700 text-sm">{item.description}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
                {/* Sell Your Car with Us */}
                <section className="py-16 border-2 border-gray-300 rounded-lg p-8">
                    <h2 className="text-3xl font-bold mb-8 text-blue-900">Sell Your Car with Us</h2>
                    <div className="grid md:grid-cols-2 gap-10 items-start">

                        {/* Left Text Content */}
                        <div className="text-gray-700 space-y-5">
                            <p>
                                Want to sell your car? MMAutoTech makes it easy. We offer good prices and a simple process.
                                Our team gives fair and honest evaluations, so you get the best deal.
                            </p>
                            <p>
                                Selling your car with us is quick and hassle-free. Bring your car in, and we handle the rest,
                                including all the paperwork. You’ll get an instant offer, so you can move on fast without any delays.
                            </p>
                            <p>
                                There’s no need to sell if you don’t want to. We give you all the information you need to make
                                the right choice for you. If you decide to upgrade, you can use your car’s value to buy a quality used car from us.
                                We have a great selection of reliable vehicles to choose from.
                            </p>
                            <p>
                                At MMAutoTech, we are known for our trustworthy and reliable service.
                                We care about our customers and want to make selling your car as easy as possible.
                                Just fill out the form on our website to get started. Once we receive your details,
                                we’ll contact you to arrange an appraisal.
                            </p>
                        </div>

                        {/* Form Section */}
                        <form
                            action="https://formspree.io/f/xovlyodw"
                            method="POST"
                            className="space-y-5 w-full"
                            encType="multipart/form-data"
                        >
                            <input
                                type="text"
                                name="name"
                                placeholder="Your Name *"
                                required
                                className="w-full border border-gray-300 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />

                            <input
                                type="tel"
                                name="phone"
                                placeholder="Your Phone Number *"
                                required
                                className="w-full border border-gray-300 rounded px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500" />

                            <select
                                name="carModel"
                                required
                                className="w-full border border-gray-300 rounded px-4 py-3 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="">Your Car Model *</option>
                                <option value="Toyota">Toyota</option>
                                <option value="Honda">Honda</option>
                                <option value="BMW">BMW</option>
                            </select>

                            {/* Upload Box */}
                            <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center text-sm text-gray-600">
                                <label htmlFor="carImages" className="cursor-pointer block text-blue-600 font-medium mb-2">
                                    Car Images *
                                </label>
                                <input
                                    id="carImages"
                                    name="images"
                                    type="file"
                                    multiple
                                    required
                                    className="hidden" />
                                <div>
                                    <svg className="mx-auto mb-2 w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M3 15a4 4 0 004 4h10a4 4 0 004-4M4 7h16M4 7v4m16-4v4M4 11h16" />
                                    </svg>
                                    <p>Click or drag files to this area to upload.</p>
                                    <p className="text-xs text-gray-400">You can upload up to 10 files.</p>
                                </div>
                            </div>

                            <button
                                type="submit"
                                className="w-full border border-blue-900 text-blue-900 font-semibold py-3 px-8 rounded hover:bg-blue-900 hover:text-white transition"
                            >
                                BOOK INSPECTION
                            </button>
                        </form>
                    </div>
                </section>


                {/* Call to Action Section */}
                <section className="py-16 bg-[#f4f6fc] text-black px-4 sm:px-6 lg:px-8">
                    <div className="max-w-xl mx-auto text-center">
                        <p className="uppercase tracking-wider text-sm mb-2 font-semibold text-gray-700">
                            Emergency Call 24/7
                        </p>
                        <p className="text-3xl sm:text-4xl font-bold text-[#111827] mb-2">
                            +44 7747 762059
                        </p>
                        <div className="w-12 h-1 bg-yellow-400 mx-auto mb-6"></div>
                        <p className="uppercase text-sm font-medium text-gray-700 mb-6 tracking-widest">
                            Book an appointment today
                        </p>
                        <Link to="/contact">
                            <button className="inline-flex items-center gap-2 bg-[#1f2e9f] text-white px-6 py-3 rounded-md font-bold shadow-md relative">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="h-5 w-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 7V3M16 7V3M3 11h18M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                </svg>
                                Book Now
                                <span className="absolute right-0 top-1/2 transform -translate-y-1/2 h-full w-1 bg-yellow-400 rounded-r-md"></span>
                            </button>
                        </Link>
                    </div>
                </section>

                <FAQSection />

            </div></>
    );
};

export default SalePurchase;
